// StatusSummary — pure shaping/normalising helpers, unit-tested directly.
import { DEFAULT_STATUS_TONES } from './constants';

/**
 * Normalise one step of the per-step breakdown to
 * `{ id, label, count, link, withheld }`. Accepts the new shape as-is and the
 * legacy widget shape `{ step_id, step_name, stat, stat_withheld, applicant_link }`.
 * A present `link` makes the breakdown row navigate to the filtered applicant
 * list, as legacy `Legends` did. A `withheld` step is one the viewer may not see
 * into: it carries no count and its link would open an empty list, so the row
 * shows a lock instead and never links.
 */
export const normalizeStep = (step = {}) => {
    const withheld = Boolean(step.withheld ?? step.stat_withheld);
    return {
        id: step.id ?? step.step_id ?? '',
        label: step.label ?? step.step_name ?? '',
        count: step.count ?? step.stat ?? 0,
        link: withheld ? null : step.link ?? step.applicant_link ?? null,
        withheld,
    };
};

/**
 * Resolve the dot tone for a status: explicit per-item tone wins, then the
 * call-site `tones` map, then the built-in defaults, and finally the raw status
 * string so unknown statuses stay styleable.
 */
export const resolveTone = (status, itemTone, tones = {}) =>
    itemTone ?? tones[status] ?? DEFAULT_STATUS_TONES[status] ?? status;

/** Normalise the items to `{ status, tone, label, count, steps }`. */
export const normalizeItems = (items, { tones = {} } = {}) =>
    (items || []).map(({ status = '', tone, label, count, steps }) => ({
        status,
        tone: resolveTone(status, tone, tones),
        label,
        count,
        steps: (steps || []).map(normalizeStep),
    }));
